import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom"; // Use react-router-dom for routing
import { getPost } from "../../services/postService";
import imgPlaceholder from "../../assets/images/resources/img-placeholder.svg";
import { BLOG_IMAGE_POOL, pickPoolImage } from "../../utils/imagePools";

const formatShortDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "short" });
};

const formatLongDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
};

const BlogDetailsMain = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getPost(slug)
      .then((res) => {
        setPost(res.post);
        setRecentPosts(res.recent_posts || []);
      })
      .catch(() => {
        setError("This post could not be found, or the Laravel API server is not running.");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <section className="blog-details">
        <div className="container">
          <p>Loading post...</p>
        </div>
      </section>
    );
  }

  if (error || !post) {
    return (
      <section className="blog-details">
        <div className="container">
          <p style={{ color: "#c0392b" }}>{error}</p>
          <Link to="/blog-list" className="thm-btn">Back to Blog</Link>
        </div>
      </section>
    );
  }

  return (
    <React.Fragment>
      {/* Blog Details Start */}
      <section className="blog-details">
        <div className="container">
          <div className="row">
            <div className="col-xl-8 col-lg-7">
              <div className="blog-details__left">
                <div className="blog-details__img-1">
                  <img
                    src={post.image || pickPoolImage(BLOG_IMAGE_POOL, post.id ?? post.slug ?? post.title) || imgPlaceholder}
                    alt={post.title}
                    onError={(e) => { e.target.onerror = null; e.target.src = imgPlaceholder; }}
                  />
                  <div className="blog-details__date">
                    <p>{formatShortDate(post.published_at)}</p>
                  </div>
                </div>
                <div className="blog-details__content">
                  <ul className="blog-list__meta list-unstyled" style={{ marginTop: "20px" }}>
                    <li>
                      <span className="icon-user"></span> By {post.author_name}
                    </li>
                    <li style={{ marginLeft: "20px" }}>
                      <span className="icon-calendar"></span> {formatLongDate(post.published_at)}
                    </li>
                  </ul>
                  <h3 className="blog-details__title-1">{post.title}</h3>
                  {post.content
                    ? post.content.split("\n").filter(Boolean).map((paragraph, i) => (
                        <p className="blog-details__text-1" key={i}>
                          {paragraph}
                        </p>
                      ))
                    : (
                      <p className="blog-details__text-1">{post.excerpt}</p>
                    )}

                  {post.category?.name && (
                    <div className="blog-details__keyword-and-social" style={{ marginTop: "30px" }}>
                      <div className="blog-details__keyword-box">
                        <span>Category:</span>
                        <div className="blog-details__keyword">
                          <Link to={`/events?category=${post.category.slug}`}>{post.category.name}</Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Comment form kept as static UI for now - not wired to a backend endpoint yet */}
                <div className="comment-one">
                  <h3 className="comment-one__title">Leave a comment</h3>
                  <p className="comment-one__text">
                    By using this form you agree with the storage and handling of your data.
                  </p>
                  <form className="contact-form-validated comment-one__form" onSubmit={(e) => e.preventDefault()} noValidate>
                    <div className="row">
                      <div className="col-xl-12">
                        <div className="comment-one__input-box">
                          <input type="text" name="name" placeholder="Name" required />
                        </div>
                      </div>
                      <div className="col-xl-12">
                        <div className="comment-one__input-box">
                          <input type="email" name="email" placeholder="Email" required />
                        </div>
                      </div>
                      <div className="col-xl-12">
                        <div className="comment-one__input-box text-message-box">
                          <textarea name="message" placeholder="Message here.." required></textarea>
                        </div>
                        <div className="comment-one__btn-box">
                          <button type="submit" className="thm-btn comment-one__btn">
                            Send Message
                            <span className="icon-arrow-right"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-xl-4 col-lg-5">
              <div className="sidebar">
                {/* Recent Posts */}
                <div className="sidebar__single sidebar__post">
                  <h3 className="sidebar__title">Recent Post</h3>
                  <ul className="sidebar__post-list list-unstyled">
                    {recentPosts.map((p) => (
                      <li key={p.id}>
                        <div className="sidebar__post-content">
                          <p className="sidebar__post-date">
                            <span className="icon-calendar"></span>
                            {formatShortDate(p.published_at)}
                          </p>
                          <h3>
                            <Link to={`/blog-details/${p.slug}`}>{p.title}</Link>
                          </h3>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Blog Details End */}
    </React.Fragment>
  );
};

export default BlogDetailsMain;
